import * as React from 'react'

import { AvailableModules } from 'generated/featureDefinitions'
import {
  GetFeatureAvailabilitiesDocument,
  ShiftManagementFeaturesFlagsDocument,
  ShiftManagementPageDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
// import type { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'

// TODO: To be removed once the migration to GraphQL is done
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import { ShiftManagement } from 'modules/shift_management/components/ShiftManagement'
import { DefaultShiftsProvider } from 'modules/shift_management/hooks/useDefaultShifts'
import { EnabledFeaturesProvider } from 'modules/shift_management/hooks/useEnabledFeatures'
import { FiltersProvider } from 'modules/shift_management/hooks/useFilters'
import { NotificationsProvider } from 'modules/shift_management/hooks/useNotifications'
import { PaginationProvider } from 'modules/shift_management/hooks/usePagination'
import { PermissionsProvider } from 'modules/shift_management/hooks/usePermissions'
import { PreviewProvider } from 'modules/shift_management/hooks/usePreview'
import { ScheduleProvider } from 'modules/shift_management/hooks/useSchedule'
import { SelectionProvider } from 'modules/shift_management/hooks/useSelection'
import { ShiftsRowsProvider } from 'modules/shift_management/hooks/useShiftsRows'
import { TemplateWeeksProvider } from 'modules/shift_management/hooks/useTemplateWeeks'
import { UIProvider } from 'modules/shift_management/hooks/useUI'
import { UnassignedShiftsProvider } from 'modules/shift_management/hooks/useUnassignedShifts'
import { ViewOptionsProvider } from 'modules/shift_management/hooks/useViewOptions'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
// TODO: Pending migration to graphql
import planningVersions, {
  PlanningVersionProvider,
} from 'modules/time_planning/store/collections/planningVersions'

const ShiftManagementMainPage = ({
  children,
  contractVersions,
}: React.PropsWithChildren<{ contractVersions: ContractVersions }>) => {
  const { loading: loadingJobs } = useAsyncData(
    () => Promise.all([jobRolesCollection.fetch(), jobLevelsCollection.fetch()]),
    []
  )
  const { loading: loadingPlanningVersions } = useAsyncData(
    () => planningVersions.fetch({ data: { for_shifts: true } }),
    []
  )

  const { company: currentCompany } = useCurrentSession()
  const { loading: loadingFeatures, data: featuresData } = useQuery(
    GetFeatureAvailabilitiesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        modules: [AvailableModules.shift_management, AvailableModules.time_balance],
        companyId: currentCompany.id,
      },
    }
  )

  const { loading: loadingFeatureFlags, data: featureFlagsData } = useQuery(
    ShiftManagementFeaturesFlagsDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        featureIds: Object.values(ShiftManagementFeatureFlags),
        companyId: currentCompany.id,
      },
    }
  )

  const { data: permissions, loading: loadingPermissions } = useQuery(
    ShiftManagementPageDocument,
    {}
  )

  const featureAvailabilities = featuresData?.companies.featureAvailabilities || []
  const featureFlags = featureFlagsData?.features.featuresConnection.nodes || []
  const isLoading =
    loadingJobs ||
    loadingPlanningVersions ||
    loadingFeatures ||
    loadingPermissions ||
    loadingFeatureFlags

  if (isLoading) return <Loading />

  return (
    <EnabledFeaturesProvider features={featureAvailabilities} featureFlags={featureFlags}>
      <PermissionsProvider permissions={permissions}>
        <TemplateWeeksProvider>
          <DefaultShiftsProvider contractVersions={contractVersions}>
            <NotificationsProvider>
              <UnassignedShiftsProvider>
                <PlanningVersionProvider collection={planningVersions}>
                  <ScheduleProvider>
                    <ViewOptionsProvider>
                      <FiltersProvider>
                        <PaginationProvider>
                          <ShiftsRowsProvider>
                            <UIProvider>
                              <SelectionProvider>
                                <PreviewProvider>
                                  <ShiftManagement contractVersions={contractVersions}>
                                    {children}
                                  </ShiftManagement>
                                </PreviewProvider>
                              </SelectionProvider>
                            </UIProvider>
                          </ShiftsRowsProvider>
                        </PaginationProvider>
                      </FiltersProvider>
                    </ViewOptionsProvider>
                  </ScheduleProvider>
                </PlanningVersionProvider>
              </UnassignedShiftsProvider>
            </NotificationsProvider>
          </DefaultShiftsProvider>
        </TemplateWeeksProvider>
      </PermissionsProvider>
    </EnabledFeaturesProvider>
  )
}

export default ShiftManagementMainPage
